import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"
import { showSystemToast } from "../utils/system_toast"

const DEMO_PIN_STORAGE_KEY = "demo_user_pins"

// Shared contact actions; row and detail controllers only adapt placement.
export default class extends Controller {
    static values = {
        contactId: String,
        cardId: String,
        contactName: String,
        demo: Boolean,
        pinned: Boolean,
        pinId: String,
        pinsUrl: String,
        linkedinUrl: String,
        memberSlugs: Array,
        discardUrl: String,
        networkUrl: String,
        editUrl: String,
        inviteUrl: String,
        initialAction: String
    }

    static targets = ["pinItem", "pinIcon", "pinLabel"]

    connect() {
        this._onPinChanged = (event) => {
            const state = event.detail
            if (state.contactId !== this.contactIdValue || state.demo !== this.demoValue) return
            this.pinnedValue = state.pinned
            this.pinIdValue = state.pinId
        }
        this._onRemoved = (event) => {
            if (event.detail.contactId !== this.contactIdValue) return
            this.element.closest(".contact-row")?.remove()
            const card = this._card()
            if (card?.classList.contains("contact-v3--page")) window.location.assign(this.networkUrlValue)
            else if (card) document.dispatchEvent(new CustomEvent("jane:detail-sidebars-close"))
        }
        document.addEventListener("contact-menu:pin-changed", this._onPinChanged)
        document.addEventListener("contact-menu:removed", this._onRemoved)
        if (this.demoValue) {
            const key = `Contact:${this.contactIdValue}`
            this.pinnedValue = Boolean(this._loadDemoPinState()[key])
            this.pinIdValue = this.pinnedValue ? key : ""
        }
        if (["edit", "invite"].includes(this.initialActionValue)) {
            this._initialActionFrame = requestAnimationFrame(() => {
                this[this.initialActionValue]()
                const url = new URL(window.location.href)
                url.searchParams.delete("contact_action")
                history.replaceState(history.state, "", url)
            })
        }
    }

    disconnect() {
        document.removeEventListener("contact-menu:pin-changed", this._onPinChanged)
        document.removeEventListener("contact-menu:removed", this._onRemoved)
        cancelAnimationFrame(this._initialActionFrame)
    }

    _card() {
        return this.element.closest(".detail-preview") || (this.cardIdValue ? document.getElementById(this.cardIdValue) : null)
    }

    edit(event) {
        event?.preventDefault()
        const card = this._card()
        if (!card) {
            if (this.editUrlValue) window.location.assign(this.editUrlValue)
            return
        }
        this.application.getControllerForElementAndIdentifier(card, "contact-edit")?.open()
    }

    invite(event) {
        event?.preventDefault()
        const card = this._card()
        if (!card) {
            if (this.inviteUrlValue) window.location.assign(this.inviteUrlValue)
            return
        }
        const dialog = card.querySelector("dialog.contact-invite-dialog")
        if (dialog && !dialog.open) dialog.showModal()
    }

    _broadcastPin() {
        document.dispatchEvent(new CustomEvent("contact-menu:pin-changed", {
            detail: { contactId: this.contactIdValue, demo: this.demoValue, pinned: this.pinnedValue, pinId: this.pinIdValue }
        }))
    }

    togglePin(e) {
        e.preventDefault()
        e.stopPropagation()

        if (this.demoValue) {
            this._toggleDemoPin()
            return
        }

        if (this.hasPinItemTarget) this.pinItemTarget.disabled = true
        let request = this.pinnedValue ? this._unpinRequest() : this._pinRequest()
        request
            .then(({ response, data }) => {
                if (!response.ok || !data.ok) throw new Error(data.error || "Could not update pin")
                this.pinnedValue = !!data.pinned
                this.pinIdValue = data.pin_id ? String(data.pin_id) : ""
                this._syncPinItem()
                this._broadcastPin()
            })
            .catch((error) => showSystemToast(error.message || "Could not update pin.", { level: "danger" }))
            .finally(() => {
                if (this.hasPinItemTarget) this.pinItemTarget.disabled = false
            })
    }

    syncLinkedin(e) {
        e.preventDefault()
        e.stopPropagation()
        if (!this.linkedinUrlValue) return

        fetch(this.linkedinUrlValue, {
            method: "POST",
            headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin"
        })
            .then((response) => response.json().then((data) => ({ response, data })))
            .then(({ response, data }) => {
                if (!response.ok || !data.ok) throw new Error(data.message || "Could not start the LinkedIn sync.")
                showSystemToast(`Syncing ${this.contactNameValue} from LinkedIn…`, { level: "info" })
            })
            .catch((error) => showSystemToast(error.message || "Could not start the LinkedIn sync.", { level: "danger" }))
    }

    // Hands off to the shared "Edit connections" modal (contact-connections
    // controller), same as the network row kebab.
    editConnections(e) {
        e.preventDefault()
        e.stopPropagation()
        document.dispatchEvent(new CustomEvent("contact-connections:open", {
            detail: { contactId: this.contactIdValue, name: this.contactNameValue, groupSlugs: this.hasMemberSlugsValue ? this.memberSlugsValue : [] }
        }))
    }

    deleteContact(e) {
        e.preventDefault()
        e.stopPropagation()
        if (!this.discardUrlValue) return
        if (!window.confirm(`Remove ${this.contactNameValue || "this contact"} from your network?`)) return

        fetch(this.discardUrlValue, {
            method: "DELETE",
            headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin"
        })
            .then((response) => response.json().then((data) => ({ response, data })))
            .then(({ response, data }) => {
                if (!response.ok || !data.ok) throw new Error(data.error || "Could not remove that contact.")
                document.dispatchEvent(new CustomEvent("contact-menu:removed", { detail: { contactId: this.contactIdValue } }))
                showSystemToast(`${this.contactNameValue || "Contact"} removed from your network.`, { level: "success" })
            })
            .catch((error) => showSystemToast(error.message || "Could not remove that contact.", { level: "danger" }))
    }

    pinnedValueChanged() {
        this._syncPinItem()
    }

    _syncPinItem() {
        if (this.hasPinIconTarget) {
            this.pinIconTarget.className = this.pinnedValue ? "bi bi-pin-fill" : "bi bi-pin"
        }
        if (this.hasPinLabelTarget) {
            this.pinLabelTarget.textContent = this.pinnedValue ? "Unpin" : "Pin"
        }
        if (this.hasPinItemTarget) {
            this.pinItemTarget.setAttribute("aria-label", this.pinnedValue ? "Unpin contact" : "Pin contact")
        }
    }

    _pinRequest() {
        return fetch(this.pinsUrlValue, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "X-CSRF-Token": csrfToken()
            },
            credentials: "same-origin",
            body: JSON.stringify({
                pinable_type: "Contact",
                pinable_id: this.contactIdValue,
                metadata: {}
            })
        }).then((response) => response.json().then((data) => ({ response, data })))
    }

    _unpinRequest() {
        let url = `${this.pinsUrlValue}/${encodeURIComponent(this.pinIdValue)}`
        return fetch(url, {
            method: "DELETE",
            headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin"
        }).then((response) => response.json().then((data) => ({ response, data })))
    }

    _toggleDemoPin() {
        let key = `Contact:${this.contactIdValue}`
        let state = this._loadDemoPinState()
        if (state[key]) {
            delete state[key]
            this.pinnedValue = false
            this.pinIdValue = ""
        } else {
            state[key] = { metadata: {} }
            this.pinnedValue = true
            this.pinIdValue = key
        }
        this._saveDemoPinState(state)
        this._broadcastPin()
    }

    _loadDemoPinState() {
        try {
            let raw = sessionStorage.getItem(DEMO_PIN_STORAGE_KEY)
            return raw ? JSON.parse(raw) : {}
        } catch {
            return {}
        }
    }

    _saveDemoPinState(state) {
        try {
            sessionStorage.setItem(DEMO_PIN_STORAGE_KEY, JSON.stringify(state))
        } catch {
            // sessionStorage unavailable — ignore
        }
    }
}
